import './Drawer.css';

import { Ui24Cross } from '@ids/react-icons';
import { useRef, useState } from 'react';

import Tagline from '../../components/tagline/Tagline';
import type { Action, Content, ContentText } from '../../global-types/Content';
import { useHandleActionClick } from '../../hooks/useHandleActionClick';
import { useIsMobile } from '../../hooks/useIsMobile';
import useTracking from '../../hooks/useTracking';
import DrawerContent from './DrawerContent';
import { useDrawerDrag } from './useDrawerDrag';
import { useDrawerFocusTrap } from './useDrawerFocusTrap';

type DrawerProps = {
  content: Content;
  viewId: number;
  dispatchActionEvent: (action: Action) => void;
};

function extractTagline(texts: ContentText[] | undefined): ContentText | undefined {
  return texts?.find((text) => text.type === 'tagline');
}

function Drawer({ content, viewId, dispatchActionEvent }: DrawerProps) {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const peekAreaRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const peekHandleRef = useRef<HTMLButtonElement>(null);
  const trackVisibilityElement = useTracking(content, viewId);
  const [disableButton, setDisableButton] = useState(false);
  const isMobile = useIsMobile();
  const { handleActionClick } = useHandleActionClick(dispatchActionEvent);

  const view = content.contentViews?.find((cv) => cv.contentViewId === viewId);
  // actions[0]/[1]/[2] are always close/minimize/open, unconditionally, for every drawer view
  const closeAction = view?.viewElements?.actions?.[0];
  const minimizeAction = view?.viewElements?.actions?.[1];
  const openAction = view?.viewElements?.actions?.[2];
  const tagline = extractTagline(view?.viewElements?.texts);
  const contentTexts = view?.viewElements?.texts?.filter((text) => text !== tagline);

  const { open, motionState, style, peekAreaHandlers, peekHandleHandlers, contentHandlers, closeDrawer } =
    useDrawerDrag({
      drawerRef,
      peekAreaRef,
      isMobile,
      onClose: closeAction ? () => void handleActionClick(closeAction) : undefined,
      onPeekToggleAction: (wasOpen) => {
        const toggleAction = wasOpen ? minimizeAction : openAction;
        if (toggleAction) void handleActionClick(toggleAction);
      },
    });

  useDrawerFocusTrap({ drawerRef, motionState, isMobile, closeDrawer });

  if (!view) return null;

  return (
    <div id="drawer" className={motionState} role="dialog" ref={drawerRef} style={style} tabIndex={-1}>
      {view.viewElements.actions && closeAction && (
        <button
          aria-label={closeAction?.text ?? 'Close'}
          className="if button medium tonal modal-v19-close-button"
          id="close-modal-v19"
          type="button"
          tabIndex={motionState === 'opening' ? 0 : -1}
          aria-hidden={motionState !== 'opening'}
          onClick={() => void handleActionClick(closeAction)}
          ref={closeButtonRef}
        >
          <Ui24Cross />
        </button>
      )}
      <div {...contentHandlers}>
        <DrawerContent
          view={view}
          texts={contentTexts}
          trackVisibilityElement={trackVisibilityElement}
          disableButton={disableButton}
          setDisableButton={setDisableButton}
          dispatchActionEvent={dispatchActionEvent}
        />
      </div>
      <div className="peek-area" ref={peekAreaRef} {...peekAreaHandlers}>
        <div className="peek-handle">
          <button
            ref={peekHandleRef}
            aria-label={open ? (minimizeAction?.text ?? 'Minimize') : (openAction?.text ?? 'Expand')}
            aria-controls="drawer"
            aria-expanded={open}
            className="peek-handle-button"
            type="button"
            tabIndex={isMobile ? 0 : -1}
            aria-hidden={!isMobile}
            {...peekHandleHandlers}
          />
        </div>
        {tagline && <Tagline text={tagline.value} color={tagline.color ?? 'green'} icon={tagline.icon} />}
      </div>
    </div>
  );
}

export default Drawer;
